// PaymentReminderPopup.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { MdClose, MdAdd, MdContentCopy } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { getMyTeamPayments, submitPayment, clearLastAction } from "../store";
import { showNotification } from "./NotificationProvider";

const StatusChip = ({ status }) => {
  const config = {
    approved: { color: "#10B981", label: "Approved" },
    pending: { color: "#F59E0B", label: "Pending" },
    canceled: { color: "#EF4444", label: "Canceled" },
  };
  const { color, label } = config[status] || {
    color: "#6B7280",
    label: "Unknown",
  };
  return (
    <Chip
      label={label}
      sx={{
        backgroundColor: color,
        color: "#fff",
        fontWeight: 700,
        fontSize: { xs: "0.9rem", sm: "0.95rem" },
        height: 28,
        px: 0.8,
      }}
    />
  );
};

const formatDate = (d) => {
  const date = new Date(d);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const CountdownTimer = ({ paymentDeadline }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    if (!paymentDeadline) return;

    const deadline = new Date(paymentDeadline);

    const timer = setInterval(() => {
      const now = new Date();
      const difference = deadline - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((difference / 1000 / 60) % 60);
        const seconds = Math.floor((difference / 1000) % 60);

        setTimeLeft({ days, hours, minutes, seconds });

        // Flash effect if less than 1 hour remaining but not completely finished
        const totalHours = days * 24 + hours;
        if (totalHours === 0 && (minutes > 0 || seconds > 0)) {
          setFlash(true);
          setTimeout(() => setFlash(false), 500);
        }
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setFlash(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [paymentDeadline]);

  if (!paymentDeadline) return null;

  const deadline = new Date(paymentDeadline);
  const formatDeadline = () => {
    return deadline.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const totalHours = timeLeft.days * 24 + timeLeft.hours;
  const isUrgent = totalHours === 0;

  return (
    <Box>
      <Typography
        sx={{
          fontSize: { xs: "1.8rem", sm: "2.5rem" },
          color: "#F59E0B",
          fontWeight: 600,
          mb: 2,
          textAlign: { xs: "center", sm: "left" },
        }}
      >
        Son Ödeme Tarihi: {formatDeadline()} 23:59
      </Typography>
      <Box
        sx={{
          display: "flex",
          gap: { xs: 1, sm: 3 },
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: { xs: "center", sm: "flex-start" },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: "2rem", sm: "2.5rem" },
            color: "#94A3B8",
            fontWeight: 700,
            width: { xs: "100%", sm: "auto" },
            textAlign: { xs: "center", sm: "left" },
          }}
        >
          Kalan Süre:
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: { xs: 0.8, sm: 2 },
            justifyContent: "center",
          }}
        >
          {[
            { value: timeLeft.days, label: "Gün" },
            { value: timeLeft.hours, label: "Saat" },
            { value: timeLeft.minutes, label: "Dakika" },
            { value: timeLeft.seconds, label: "Saniye" },
          ].map((item, idx) => (
            <Box
              key={idx}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 65, sm: 100 },
                height: { xs: 65, sm: 100 },
                p: { xs: 0.5, sm: 2 },
                background: "rgba(15, 23, 42, 0.6)",
                borderRadius: 2,
                border:
                  isUrgent && flash
                    ? "3px solid rgba(239, 68, 68, 1)"
                    : isUrgent
                    ? "2px solid rgba(239, 68, 68, 0.6)"
                    : "1px solid rgba(245, 158, 11, 0.3)",
                transition: "all 0.3s ease",
                boxShadow:
                  isUrgent && flash
                    ? "0 0 30px rgba(239, 68, 68, 0.9), 0 0 60px rgba(239, 68, 68, 0.5)"
                    : isUrgent
                    ? "0 0 15px rgba(239, 68, 68, 0.4)"
                    : "none",
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: "1.8rem", sm: "5rem" },
                  fontWeight: 800,
                  color: isUrgent ? "#EF4444" : "#F59E0B",
                  lineHeight: 1,
                  fontFamily: "monospace",
                }}
              >
                {String(item.value).padStart(2, "0")}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: "0.7rem", sm: "1.5rem" },
                  color: "#94A3B8",
                  mt: { xs: 0.3, sm: 1 },
                  fontWeight: 600,
                }}
              >
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const SubmitPaymentDialog = ({
  open,
  onClose,
  onSubmit,
  remainingAmount = 0,
  isSubmitting,
  usdtWalletAddress = null,
  usdtNetwork = null,
}) => {
  const [amount, setAmount] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    if (!open) {
      setAmount("");
    }
  }, [open]);

  const handleSubmit = () => {
    const parsed = Number(String(amount).replace(/,/g, ""));
    if (!parsed || parsed <= 0) {
      showNotification("Error", "error", "Please enter a valid amount.");
      return;
    }
    onSubmit({ amount: parsed });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          backgroundColor: "#0F172A",
          color: "#F1F5F9",
          borderRadius: isMobile ? 0 : 3,
          boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)",
          border: "1px solid #1E293B",
        },
      }}
    >
      <Box
        sx={{
          p: 3,
          borderBottom: "1px solid #334155",
          background: "linear-gradient(135deg, #1E293B 0%, #0F172A 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{ fontSize: "1.8rem", fontWeight: 700, color: "#F1F5F9" }}
        >
          Submit Payment
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "#94A3B8" }}>
          <MdClose size={28} />
        </IconButton>
      </Box>
      <DialogContent sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography
              sx={{
                mb: 1,
                color: "#94A3B8",
                fontSize: "1.2rem",
                fontWeight: 600,
              }}
            >
              Remaining Amount
            </Typography>
            <Typography
              sx={{ fontSize: "2rem", fontWeight: 700, color: "#10B981" }}
            >
              ${Number(remainingAmount || 0).toLocaleString("en-US")} USD
            </Typography>
          </Box>

          {usdtWalletAddress && (
            <Box
              sx={{
                p: 2.5,
                backgroundColor: "#1E293B",
                borderRadius: 2,
                border: "1px solid #10B981",
              }}
            >
              <Typography
                sx={{
                  mb: 1.5,
                  color: "#10B981",
                  fontSize: "1.3rem",
                  fontWeight: 700,
                  textTransform: "uppercase",
                }}
              >
                Payment Address {usdtNetwork && `(${usdtNetwork})`}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, alignItems: "stretch" }}>
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: "#0F172A",
                    borderRadius: 1,
                    border: "1px solid #475569",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "1.3rem",
                      fontWeight: 600,
                      color: "#F1F5F9",
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      lineHeight: 1.5,
                    }}
                  >
                    {usdtWalletAddress}
                  </Typography>
                </Box>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(usdtWalletAddress);
                    showNotification(
                      "Success",
                      "success",
                      "Address copied to clipboard!"
                    );
                  }}
                  variant="contained"
                  sx={{
                    minWidth: "auto",
                    width: 56,
                    height: "auto",
                    backgroundColor: "#10B981",
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "#059669",
                    },
                  }}
                >
                  <MdContentCopy size={24} />
                </Button>
              </Box>
            </Box>
          )}

          <TextField
            label="Payment Amount (USD)"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0"
            fullWidth
            required
            inputProps={{ inputMode: "decimal" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: "1.6rem",
                color: "#F1F5F9",
                backgroundColor: "#1E293B",
                "& fieldset": {
                  borderColor: "#475569",
                },
                "&:hover fieldset": {
                  borderColor: "#64748B",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#10B981",
                },
              },
              "& .MuiInputLabel-root": {
                color: "#94A3B8",
                fontSize: "1.4rem",
                "&.Mui-focused": {
                  color: "#10B981",
                },
              },
            }}
          />

          <Stack direction="row" spacing={2}>
            <Button
              onClick={onClose}
              variant="outlined"
              fullWidth
              sx={{
                height: 56,
                fontSize: "1.2rem",
                fontWeight: 600,
                color: "#94A3B8",
                borderColor: "#475569",
                "&:hover": {
                  borderColor: "#64748B",
                  backgroundColor: "#1E293B",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              sx={{
                height: 56,
                fontSize: "1.2rem",
                fontWeight: 700,
                backgroundColor: "#10B981",
                boxShadow: "0 8px 16px rgba(16, 185, 129, 0.4)",
                "&:hover": {
                  backgroundColor: "#059669",
                  boxShadow: "0 12px 24px rgba(16, 185, 129, 0.5)",
                },
                "&:disabled": {
                  backgroundColor: "#475569",
                  color: "#64748B",
                },
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                "Submit Payment"
              )}
            </Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

const PaymentReminderPopup = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentUser = useSelector((s) => s.user.user?.user);
  const myTeamPeriods = useSelector((s) => s.teamPayment.myTeamPeriods);
  const isSubmitting = useSelector((s) => s.teamPayment.isSubmitting);
  const lastAction = useSelector((s) => s.teamPayment.lastAction);

  const [mainOpen, setMainOpen] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);

  const shouldShow = useMemo(() => {
    const t = currentUser?.team;
    const permission = currentUser?.panelPermission;

    // If permission is "waiting" or null, don't show popup
    if (!permission || permission === "waiting") return false;

    // Show popup only for "warning" or "blocked"
    return Boolean(
      t?.paymentEnabled &&
        t?.monthlyFee &&
        (permission === "warning" || permission === "blocked")
    );
  }, [currentUser]);

  const currentPeriod = useMemo(() => {
    if (!myTeamPeriods?.length) return null;
    return myTeamPeriods.find((p) => p.status === "active") || myTeamPeriods[0];
  }, [myTeamPeriods]);

  const fetchData = useCallback(async () => {
    if (!shouldShow) return;
    try {
      await dispatch(getMyTeamPayments()).unwrap();
    } catch (e) {
      console.error(e);
    }
  }, [dispatch, shouldShow]);

  useEffect(() => {
    if (shouldShow) fetchData();
  }, [shouldShow, fetchData]);

  useEffect(() => {
    if (currentPeriod && shouldShow) {
      setMainOpen(true);
    }
  }, [currentPeriod, shouldShow]);

  // Keep popup open if deadline has passed
  useEffect(() => {
    const checkDeadline = () => {
      if (!currentUser?.team?.paymentDeadline || !shouldShow) return;

      const deadline = new Date(currentUser.team.paymentDeadline);
      const now = new Date();

      if (deadline < now) {
        setMainOpen(true);
      }
    };

    // Check immediately
    checkDeadline();

    // Check every second to ensure popup stays open when deadline passes
    const interval = setInterval(checkDeadline, 1000);

    return () => clearInterval(interval);
  }, [currentUser?.team?.paymentDeadline, shouldShow]);

  useEffect(() => {
    if (lastAction === "payment_submitted") {
      fetchData();
      dispatch(clearLastAction());
    }
  }, [lastAction, fetchData, dispatch]);

  const handleSubmit = async ({ amount }) => {
    try {
      await dispatch(submitPayment({ amount: Number(amount) })).unwrap();
      showNotification("Success", "success", "Payment submitted successfully.");
      setSubmitOpen(false);
    } catch (error) {
      console.error(error);
      showNotification("Error", "error", error || "Failed to submit payment.");
    }
  };

  if (!shouldShow || !currentPeriod) return null;

  const payments = currentPeriod.payments || [];
  const requiredAmount = Number(currentPeriod.requiredAmount || 0);
  const paidAmount = Number(currentPeriod.paidAmount || 0);

  // Check if user is blocked from closing the popup
  const isBlocked = currentUser?.panelPermission === "blocked";

  // Check if payment deadline has passed
  const isDeadlinePassed = currentUser?.team?.paymentDeadline
    ? new Date(currentUser.team.paymentDeadline) < new Date()
    : false;

  // User cannot close popup if blocked OR if deadline has passed
  const cannotClose = isBlocked || isDeadlinePassed;

  return (
    <>
      <Dialog
        open={mainOpen}
        onClose={cannotClose ? undefined : () => setMainOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        disableEscapeKeyDown={cannotClose}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
          },
        }}
        PaperProps={{
          sx: {
            background: cannotClose
              ? "linear-gradient(135deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)"
              : "linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 41, 59, 0.85) 100%)",
            backdropFilter: "blur(20px) saturate(180%)",
            WebkitBackdropFilter: "blur(20px) saturate(180%)",
            color: "#F1F5F9",
            borderRadius: isMobile ? 0 : 3,
            boxShadow: cannotClose
              ? "0 25px 50px -12px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(239, 68, 68, 0.3)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
            border: cannotClose
              ? "1px solid rgba(239, 68, 68, 0.5)"
              : "1px solid rgba(255, 255, 255, 0.18)",
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            background: cannotClose
              ? "linear-gradient(135deg, rgba(153, 27, 27, 0.6) 0%, rgba(127, 29, 29, 0.6) 100%)"
              : "linear-gradient(135deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.6) 100%)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: { xs: "1.8rem", sm: "2.2rem" },
                fontWeight: 700,
                color: "#F1F5F9",
              }}
            >
              {cannotClose ? "⚠️ Payment Required" : "Monthly Payment"}
            </Typography>
            {cannotClose && (
              <Typography
                sx={{
                  fontSize: "1.2rem",
                  color: "#FCA5A5",
                  mt: 0.5,
                }}
              >
                Your payment is overdue. Please complete payment to continue.
              </Typography>
            )}
          </Box>
          {!cannotClose && (
            <IconButton
              onClick={() => setMainOpen(false)}
              size="small"
              sx={{ color: "#94A3B8" }}
            >
              <MdClose size={28} />
            </IconButton>
          )}
        </Box>

        <DialogContent
          sx={{
            p: { xs: 2, sm: 3 },
            maxHeight: "85vh",
            overflow: "auto",
            // Hide scrollbar
            "&::-webkit-scrollbar": {
              display: "none",
            },
            scrollbarWidth: "none", // Firefox
            msOverflowStyle: "none", // IE and Edge
          }}
        >
          <Stack spacing={2.5}>
            {/* Countdown Timer & Team Info - Side by Side on Desktop */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: { xs: 2.5, md: 0 },
                p: { xs: 2, sm: 2.5 },
                background:
                  "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(251, 191, 36, 0.1) 100%)",
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                borderRadius: 2.5,
                border: "1px solid rgba(245, 158, 11, 0.4)",
                boxShadow:
                  "0 8px 32px rgba(245, 158, 11, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.1)",
              }}
            >
              {/* Left Side - Countdown Timer */}
              <Box sx={{ flex: 1 }}>
                <CountdownTimer
                  paymentDeadline={currentUser?.team?.paymentDeadline}
                />
              </Box>

              {/* Divider - Only on Desktop */}
              <Divider
                orientation="vertical"
                flexItem
                sx={{
                  display: { xs: "none", md: "block" },
                  mx: 2.5,
                  borderColor: "rgba(245, 158, 11, 0.3)",
                  borderWidth: 1,
                }}
              />

              {/* Right Side - Team Info */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: "1.6rem", sm: "2rem" },
                    color: "#94A3B8",
                    mb: 2,
                    lineHeight: 1.4,
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  Ödeme sayfası sadece{" "}
                  <span style={{ color: "#F59E0B" }}>Team Adminlerine</span>{" "}
                  gösterilir.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: { xs: 2, sm: 3 },
                    alignItems: { xs: "center", sm: "flex-start" },
                    flexWrap: "wrap",
                    justifyContent: { xs: "center", sm: "flex-start" },
                  }}
                >
                  {/* Team Name */}
                  <Box
                    sx={{
                      flex: "0 1 auto",
                      minWidth: { xs: 120, sm: 150 },
                      textAlign: { xs: "center", sm: "left" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "1.5rem", sm: "2rem" },
                        color: "#64748B",
                        mb: 0.5,
                        fontWeight: 600,
                      }}
                    >
                      Team Adı:
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem" },
                        color: "#10B981",
                        fontWeight: 700,
                      }}
                    >
                      {currentUser?.team?.teamName}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Amount Container with Warning on Top */}
            <Box>
              {/* Warning Text - Top Part */}
              <Box
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  background: "rgba(245, 158, 11, 0.2)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  borderTopLeftRadius: 2.5,
                  borderTopRightRadius: 2.5,
                  border: "1px solid rgba(245, 158, 11, 0.4)",
                  borderBottom: "none",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    color: "#FDE68A",
                    fontWeight: 600,
                    lineHeight: 1.5,
                    mb: 0.8,
                  }}
                >
                  ⚠️ Lütfen aşağıdaki tutarda ki ödemeyi tamamlayınız. Ödemeler
                  sistem tarafından otomatik onaylanır.
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: "1.5rem", sm: "2rem" },
                    color: "#FCA5A5",
                    fontWeight: 600,
                    lineHeight: 1.5,
                  }}
                >
                  🔒 Son ödeme tarihi dolduğunda panel erişimi kapatılacaktır.
                </Typography>
              </Box>

              {/* Amount Display & Pay Button - Bottom Part */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 3,
                  p: { xs: 2.5, sm: 4 },
                  background:
                    "linear-gradient(135deg, rgba(30, 41, 59, 0.5) 0%, rgba(51, 65, 85, 0.5) 100%)",
                  backdropFilter: "blur(10px)",
                  WebkitBackdropFilter: "blur(10px)",
                  borderBottomLeftRadius: 2.5,
                  borderBottomRightRadius: 2.5,
                  borderLeft: "1px solid rgba(255, 255, 255, 0.15)",
                  borderRight: "1px solid rgba(255, 255, 255, 0.15)",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.15)",
                  boxShadow:
                    "0 10px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: { xs: "1.8rem", sm: "2rem" },
                      color: "#94A3B8",
                      mb: 2,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.1em",
                    }}
                  >
                    AMOUNT
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                      fontWeight: 800,
                      color: "#F1F5F9",
                      lineHeight: 1.1,
                      fontFamily: "monospace",
                    }}
                  >
                    {requiredAmount.toLocaleString("en-US")}{" "}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem" },
                        fontWeight: 700,
                        color: "#10B981",
                      }}
                    >
                      USDT
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: "2.2rem", sm: "3rem", md: "3.5rem" },
                        fontWeight: 800,
                        color: "#64748B",
                        mx: 1,
                      }}
                    >
                      /
                    </Typography>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: { xs: "2.2rem", sm: "3rem", md: "3.5rem" },
                        fontWeight: 800,
                        color:
                          paidAmount >= requiredAmount ? "#10B981" : "#EF4444",
                      }}
                    >
                      {paidAmount.toLocaleString("en-US")}
                    </Typography>
                  </Typography>
                  <Typography
                    sx={{
                      mt: 2,
                      fontSize: { xs: "1.2rem", sm: "1.4rem" },
                      color: "#CBD5E1",
                      fontWeight: 600,
                    }}
                  >
                    Required / Paid
                  </Typography>
                </Box>

                <Button
                  onClick={() => setSubmitOpen(true)}
                  disabled={paidAmount >= requiredAmount}
                  variant="contained"
                  startIcon={<MdAdd size={32} />}
                  sx={{
                    height: { xs: 64, sm: 72 },
                    px: { xs: 4, sm: 5 },
                    fontSize: { xs: "1.4rem", sm: "1.6rem" },
                    fontWeight: 800,
                    backgroundColor: "#10B981",
                    color: "#fff",
                    borderRadius: 2,
                    textTransform: "none",
                    boxShadow: "0 8px 16px rgba(16, 185, 129, 0.4)",
                    "&:hover": {
                      backgroundColor: "#059669",
                      boxShadow: "0 12px 24px rgba(16, 185, 129, 0.5)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.2s ease",
                    "&:disabled": {
                      backgroundColor: "#475569",
                      color: "#64748B",
                      boxShadow: "none",
                    },
                  }}
                >
                  PAY
                </Button>
              </Box>
            </Box>

            {/* Payment History Table */}
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: "1.4rem", sm: "1.5rem" },
                  fontWeight: 700,
                  color: "#F1F5F9",
                  mb: 2,
                }}
              >
                Payment History
              </Typography>

              {payments.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    color: "#94A3B8",
                    background: "rgba(30, 41, 59, 0.4)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                    borderRadius: 2,
                    border: "1px dashed rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <Typography sx={{ fontSize: "1.2rem" }}>
                    No payments submitted yet
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    boxShadow:
                      "0 10px 40px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
                    maxHeight: { xs: 250, sm: 300 },
                    overflow: "auto",
                    background: "rgba(30, 41, 59, 0.4)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  }}
                >
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{
                            background: "rgba(51, 65, 85, 0.6)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            color: "#94A3B8",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.05rem" },
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            py: 1.5,
                          }}
                        >
                          Amount
                        </TableCell>
                        <TableCell
                          sx={{
                            background: "rgba(51, 65, 85, 0.6)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            color: "#94A3B8",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.05rem" },
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            py: 1.5,
                          }}
                        >
                          Status
                        </TableCell>
                        <TableCell
                          sx={{
                            background: "rgba(51, 65, 85, 0.6)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            color: "#94A3B8",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.05rem" },
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            py: 1.5,
                          }}
                        >
                          Date
                        </TableCell>
                        <TableCell
                          sx={{
                            background: "rgba(51, 65, 85, 0.6)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            color: "#94A3B8",
                            fontWeight: 700,
                            fontSize: { xs: "1rem", sm: "1.05rem" },
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                            py: 1.5,
                          }}
                        >
                          Notes
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {payments.map((payment, idx) => (
                        <TableRow
                          key={payment._id}
                          sx={{
                            "&:hover": {
                              background: "rgba(51, 65, 85, 0.5)",
                              backdropFilter: "blur(10px)",
                            },
                            background: "rgba(30, 41, 59, 0.3)",
                            borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                          }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              fontSize: { xs: "1.05rem", sm: "1.1rem" },
                              color: "#10B981",
                              py: 1.5,
                              borderBottom:
                                "1px solid rgba(255, 255, 255, 0.05)",
                              background: "transparent",
                            }}
                          >
                            $
                            {Number(payment.amount || 0).toLocaleString(
                              "en-US",
                              {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              }
                            )}
                          </TableCell>
                          <TableCell
                            sx={{
                              py: 1.5,
                              borderBottom:
                                "1px solid rgba(255, 255, 255, 0.05)",
                              background: "transparent",
                            }}
                          >
                            <StatusChip status={payment.status} />
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#94A3B8",
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                              py: 1.5,
                              borderBottom:
                                "1px solid rgba(255, 255, 255, 0.05)",
                              background: "transparent",
                            }}
                          >
                            {formatDate(payment.createdAt)}
                          </TableCell>
                          <TableCell
                            sx={{
                              color: "#CBD5E1",
                              fontSize: { xs: "0.95rem", sm: "1rem" },
                              py: 1.5,
                              borderBottom:
                                "1px solid rgba(255, 255, 255, 0.05)",
                              background: "transparent",
                            }}
                          >
                            {payment.notes || "-"}
                            {payment.rejectionReason && (
                              <Typography
                                component="div"
                                sx={{
                                  color: "#EF4444",
                                  fontSize: "0.9rem",
                                  mt: 0.5,
                                  fontWeight: 600,
                                }}
                              >
                                Rejected: {payment.rejectionReason}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Submit Payment Dialog */}
      <SubmitPaymentDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        onSubmit={handleSubmit}
        remainingAmount={requiredAmount - paidAmount}
        isSubmitting={isSubmitting}
        usdtWalletAddress={currentUser?.team?.usdtWalletAddress}
        usdtNetwork={currentUser?.team?.usdtNetwork}
      />
    </>
  );
};

export default PaymentReminderPopup;
